import React from 'react';
import ReactTimeAgo from 'react-time-ago';

import { ActivityLog } from '@/models/ActivityLogsByWorkspace';
import { CustomCard } from '@/modules/Dashboard/DashboardPage/Components/MetricComponents';

interface RecentActivitySectionProps {
  activities?: ActivityLog[];
  changeActiveStep: (step: string) => void;
}

function activityTimeMs(activity: ActivityLog): number {
  const t = activity.timestamp;
  if (typeof t === 'number' && !Number.isNaN(t) && t > 0) return t;
  if (activity.createdAt) {
    const ms = new Date(activity.createdAt).getTime();
    if (!Number.isNaN(ms) && ms > 0) return ms;
  }
  return 0;
}

const RecentActivitySection: React.FC<RecentActivitySectionProps> = ({ activities = [], changeActiveStep }) => {
  const recentActivities = activities
    .filter((a) => activityTimeMs(a) > 0)
    .sort((a, b) => activityTimeMs(b) - activityTimeMs(a))
    .slice(0, 5);

  // Format event type
  const formatEventType = (eventType: string): string => {
    if (!eventType) return 'Activity';
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <CustomCard title="Recent Activity">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-custom-darkSilver text-sm">Last 5 user activities</p>

        <button
          onClick={() => changeActiveStep('Activities')}
          className="text-custom-blue px-3 py-0.5 text-sm font-semibold transition-colors border-b-2 border-white hover:border-custom-blue"
        >
          View All →
        </button>
      </div>

      {/* Activities Table */}
      <div className="overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {recentActivities.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-custom-darkSilver mb-1 font-semibold">No recent activities</div>
            <div className="text-custom-darkSilver text-sm">
              Activities will appear here when users interact with the system
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="border-b border-custom-lightSilver px-4 py-2 text-left text-sm text-custom-darkSilver font-medium min-w-[120px] max-w-[300px] w-full truncate">
                  Event Type
                </th>
                <th className="border-b border-custom-lightSilver px-4 py-2 text-left text-sm text-custom-darkSilver font-medium min-w-[120px] max-w-[300px] w-full truncate">
                  Timestamp
                </th>
              </tr>
            </thead>
            <tbody>
              {recentActivities.map((activity) => (
                <tr key={activity.id} className="text-sm hover:bg-custom-blue/5 h-10">
                  <td className="px-4 truncate text-custom-darkSilver bg-white">
                    <span>{formatEventType(activity.eventType)}</span>
                  </td>
                  <td className="px-4 truncate text-custom-darkSilver bg-white">
                    {activityTimeMs(activity) > 0 ? (
                      <div className="flex flex-col">
                        <span>{new Date(activityTimeMs(activity)).toLocaleString()}</span>
                        <span className="text-xs text-custom-darkSilver">
                          <ReactTimeAgo date={new Date(activityTimeMs(activity))} locale="en-US" />
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </CustomCard>
  );
};

export default RecentActivitySection;
